import { ApplePayProvisionData, PendingCardToken, ReactNativeProps } from '@eika-infrastruktur/mobilbank-lib';
import { PaymentNetworkType } from '@eika-infrastruktur/mobilbank-lib/src/nativeEvents';

import { Logger } from '@/domain/app';

import {
    ActivationCardToken,
    ApplePayEligibleCard,
    Card,
    CardStatus,
    CardType,
    CreditCard,
    CreditCardStatus,
    CreditCardToken,
    CreditCardTokenStatus,
    DeviceType,
    WalletCreditCard,
} from '@generated-types';

import { ApplePayKbCardData, ApplePayTeCardData } from '../components/apple-pay/ApplePayContent';
import { ApplePayCardWithNativeState, PendingCardTokenKb, PendingCardTokensList, PendingCardTokenTe } from '../types';
import { getPendingCardTokensForDeviceKb } from '../utils/get-apple-pay-status';
import { isEqualOrHigherVersion } from '../utils/native-version-checker';
import { getPendingCardTokensForDevice } from '../utils/pending-cards-mapper';

const isEligibleCreditCard = (card: ApplePayEligibleCard | WalletCreditCard): card is WalletCreditCard => 'id' in card;

const getEligibleCardId = (card: ApplePayEligibleCard | WalletCreditCard) =>
    isEligibleCreditCard(card) ? card.id : card.eikaCardId;

const pendingTokenStatusesList = [CreditCardTokenStatus.INACTIVE];

const getPendingTokens = (walletCard: WalletCreditCard): CreditCardToken[] =>
    walletCard.tokens.filter((token) => pendingTokenStatusesList.includes(token.status));

const getActiveTokens = (walletCard: WalletCreditCard): CreditCardToken[] =>
    walletCard.tokens.filter((token) => token.status === 'ACTIVE');

const pairToProvisioningPayload = (pair: PairedCreditCards | ApplePayKbCardData): ApplePayProvisionData => {
    return {
        cardId: pair.card.eikaCardId,
        cardholderName: `${pair.card.cardHolder.firstName} ${pair.card.cardHolder.lastName}`,
        paymentNetwork: 'VISA',
        primaryAccountIdentifier: pair.eligibleCard.tokens.find((x) => x.panReferenceId)?.panReferenceId?.toString(),
        primaryAccountSuffix: pair.card.cardNumber.slice(-4),
    };
};

const toProvisioningPayload = (item: ApplePayCardWithNativeState | ApplePayTeCardData): ApplePayProvisionData => {
    return {
        cardId: item.eligible.eikaCardId,
        cardholderName: item.eligible.customerName,
        paymentNetwork: item.eligible.cardVendor.toString() as PaymentNetworkType,
        primaryAccountIdentifier: item.eligible.tokens.find((x) => x.panUniqueReference)?.panUniqueReference,
        primaryAccountSuffix: item.eligible.cardLast4Digits,
    };
};

export type PairedCreditCards = {
    eligibleCard: WalletCreditCard;
    card: CreditCard;
};

type PairedCards = {
    eligible: ApplePayEligibleCard;
    card: Card;
};

const canEnrollCreditCard = (card: CreditCard) => card.status === CreditCardStatus.NORMAL;
const canEnrollCard = (card: Card) => card.status === CardStatus.OK || card.status === CardStatus.NOT_ACTIVATED;

const pairCards = (cards: Card[], eligibleCards: ApplePayEligibleCard[]): PairedCards[] => {
    return (
        eligibleCards
            .map((eligible) => ({
                eligible,
                card: cards.find((card) => card.id === eligible.eikaCardId),
            }))
            .filter((pair) => !!pair.card) as PairedCards[]
    ).filter(({ card }) => canEnrollCard(card));
};

const pairCreditCards = (cards: CreditCard[], eligibleCards: WalletCreditCard[]): PairedCreditCards[] => {
    return (
        eligibleCards
            .map((eligibleCard) => ({
                eligibleCard,
                card: cards.find((card) => card.id === eligibleCard.id),
            }))
            .filter((pair) => !!pair.card) as PairedCreditCards[]
    ).filter(({ card }) => canEnrollCreditCard(card));
};

const pairKbPendingTokens = (
    cards: CreditCard[],
    walletCards: WalletCreditCard[],
    nativeTokens: PendingCardToken[],
): PendingCardTokenKb[] => {
    const pendingTokens = walletCards
        .flatMap((card) => card.tokens)
        .filter((token) => pendingTokenStatusesList.includes(token.status));

    const tokens = getPendingCardTokensForDeviceKb(pendingTokens, nativeTokens);

    const pairedTokens = tokens
        .map((token) => ({ token, card: cards.find((card) => card.id === token.cardId), source: 'kb' as const }))
        .filter(({ card }) => !!card) as unknown as PendingCardTokenKb[];

    Logger.info('wallet', 'AppleWalletKB info log', 'pairKbPendingTokens', {
        data: {
            walletCards,
            nativeTokens,
            pairedTokens,
        },
    });

    return pairedTokens;
};

const pairTePendingTokens = (
    cards: Card[],
    pendingTokens: ActivationCardToken[],
    nativeTokens: PendingCardToken[],
): PendingCardTokenTe[] => {
    const tokens = getPendingCardTokensForDevice(pendingTokens, nativeTokens);

    const pending = tokens
        .map((token) => ({ token, card: cards.find((card) => card.id === token.eikaCardId), source: 'te' as const }))
        .filter(({ card }) => !!card) as PendingCardTokenTe[];

    return pending;
};

const groupPendingTokens = (tokens: PendingCardTokensList): Record<DeviceType, PendingCardTokensList> => {
    return tokens.reduce(
        (result, item) => {
            if (item.source === 'te') {
                if (result[item.token.deviceType]) {
                    result[item.token.deviceType].push(item);
                } else {
                    result[item.token.deviceType] = [item];
                }
            } else {
                // eslint-disable-next-line no-lonely-if
                if (result[item.token.device.type as DeviceType]) {
                    result[item.token.device.type as DeviceType].push(item);
                } else {
                    result[item.token.device.type as DeviceType] = [item];
                }
            }
            return result;
        },
        {} as Record<DeviceType, PendingCardTokensList>,
    );
};

const DEFAULT_COBADGE_DEBIT_CARD_MIN_VERSION = '17.0.0';

const canEnrollTeCard = (
    nativeProps: ReactNativeProps,
    card: Card,
    minVersionDebitcardApplepay: string | undefined,
) => {
    if (card.status !== CardStatus.OK && card.status !== CardStatus.NOT_ACTIVATED) return false;

    if (
        card.type === CardType.DEBIT &&
        !isEqualOrHigherVersion(
            nativeProps.deviceInfo?.systemVersion,
            minVersionDebitcardApplepay ?? DEFAULT_COBADGE_DEBIT_CARD_MIN_VERSION,
        )
    )
        return false;
    return Boolean(nativeProps.supports?.applePay);
};

export const AppleWalletService = {
    getEligibleCardId,
    isEligibleCreditCard,
    toProvisioningPayload,
    pairCreditCards,
    pairKbPendingTokens,
    pairTePendingTokens,
    groupPendingTokens,
    pairCards,
    pairToProvisioningPayload,
    canEnrollTeCard,
    getPendingTokens,
    getActiveTokens,
};
